import { cutToast } from 'utility/toast';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import { InvoiceSigningUrgentUpdateInput } from 'common/types/globalTypes';
import React, { useState } from 'react';

import {
  PayCycleUrgentUpdate,
  PayCycleUrgentUpdateVariables,
} from './__generated__/PayCycleUrgentUpdate';
import { useStyles } from './index.styles';
import { GetPayCycleDetails } from '../__generated__/GetPayCycleDetails';

import INVOICE_SIGNING_URGENCY_UPDATE from './PayCycleUrgentUpdate.graphql';
interface UrgencyFolderProps {
  payCycleDetails?: GetPayCycleDetails;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  payCycleDetails,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(payCycleDetails?.payCycle?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [invoiceSigningUrgencyUpdate] = useMutation<
    PayCycleUrgentUpdate,
    PayCycleUrgentUpdateVariables
  >(INVOICE_SIGNING_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      payCycleDetails?.payCycle?._urgencyLevel === 0 ? 1 : 0;

    const { errors } = await invoiceSigningUrgencyUpdate({
      variables: {
        input: {
          entityId: payCycleDetails?.payCycle?.id!,
          rowTimestamp: payCycleDetails?.payCycle?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as InvoiceSigningUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!payCycleDetails?.payCycle?.id) {
          cache.modify({
            id: cache.identify({ ...payCycleDetails.payCycle }),
            fields: {
              _urgencyLevel: () => {
                return data?.payCycleUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else {
      cutToast.success('Urgency status updated successfully.');
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
